/**
 * @waw/validation-schemas — schémas zod partagés entre l'API et les fronts.
 * Règles clés :
 *  - Téléphone marocain : +212XXXXXXXXX ou 0XXXXXXXXX (mobiles 6/7, fixes 5).
 *  - Dates 'YYYY-MM-DD', heures 'HH:MM'.
 */
import { z } from 'zod';

// ─── Briques de base ──────────────────────────────────────────────────────────

export const moroccanPhoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s.-]/g, ''))
  .pipe(
    z.string().regex(
      /^(?:\+212|0)[5-7]\d{8}$/,
      'Téléphone marocain invalide (+2126XXXXXXXX ou 06XXXXXXXX)'
    )
  );

export const emailSchema = z.string().trim().toLowerCase().email('Email invalide');

export const dateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide (format YYYY-MM-DD)');

export const timeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure invalide (format HH:MM)');

// Heure optionnelle : une chaîne vide (input time non renseigné) est traitée comme « omise ».
export const optionalTimeSchema = z.preprocess(
  (v) => (v === '' || v == null ? undefined : v),
  timeSchema.optional(),
);

export const uuidSchema = z.string().uuid();

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

// ─── Réservation (tunnel public) ─────────────────────────────────────────────

export const reservationPayloadSchema = z.object({
  site: z.string().trim().min(1, 'Site requis'),
  eventType: z.string().trim().min(1, "Type d'événement requis"),
  date: dateSchema,
  startTime: timeSchema,
  participants: z.coerce.number().int('Nombre de participants invalide').min(1),
  customer: z.object({
    name: z.string().trim().min(2, 'Nom requis').max(200),
    email: emailSchema,
    phone: moroccanPhoneSchema,
  }),
  specialRequest: z.string().trim().max(2000).nullish(),
  captchaToken: z.string().max(4096).nullish(),
});

// ─── Blocage de dates (admin) ────────────────────────────────────────────────

export const blockedDatePayloadSchema = z
  .object({
    // Pour un staff_manager, site_id est injecté/écrasé par enforceSiteScope.
    site_id: uuidSchema.optional(),
    site: z.string().trim().min(1).optional(), // code site (super_admin)
    eventType: z.string().trim().min(1).nullish(), // null = tout le site
    date: dateSchema,
    startTime: optionalTimeSchema,
    endTime: optionalTimeSchema,
    reason: z
      .string({ required_error: 'La raison du blocage est obligatoire' })
      .trim()
      .min(3, 'La raison du blocage est obligatoire'),
  })
  .refine((v) => (v.startTime == null) === (v.endTime == null), {
    message: 'startTime et endTime doivent être fournis ensemble (ou omis ensemble)',
  })
  .refine((v) => v.startTime == null || v.endTime > v.startTime, {
    message: 'endTime doit être postérieur à startTime',
  });

// ─── Configuration type × site (Super Admin) ─────────────────────────────────

export const eventTypeConfigPayloadSchema = z
  .object({
    site: z.string().trim().min(1, 'Site requis'),
    eventType: z.string().trim().min(1, "Type d'événement requis"),
    capacityTotal: z.coerce.number().int().min(1).nullish(),
    slotDurationMinutes: z.coerce.number().int().min(1).nullish(),
    minParticipants: z.coerce.number().int().min(1).nullish(),
    maxParticipants: z.coerce.number().int().min(1).nullish(),
    pricePerParticipant: z.coerce.number().min(0).nullish(),
    // Tarif « prix de base + supplément » (optionnel ; prioritaire sur pricePerParticipant)
    basePrice: z.coerce.number().min(0).nullish(),
    baseParticipants: z.coerce.number().int().min(1).nullish(),
    extraParticipantPrice: z.coerce.number().min(0).nullish(),
    cancellationWindowHours: z.coerce.number().int().min(0).nullish(),
  })
  .refine(
    (v) =>
      v.minParticipants == null || v.maxParticipants == null || v.maxParticipants >= v.minParticipants,
    { message: 'maxParticipants doit être >= minParticipants' }
  );

/**
 * Calcule le montant total d'une réservation.
 *  - Si basePrice est défini : total = basePrice + max(0, participants - baseParticipants) * extraParticipantPrice
 *  - Sinon si pricePerParticipant est défini : total = pricePerParticipant * participants
 *  - Sinon : null (non tarifé)
 * Accepte des champs en camelCase (front) ou snake_case (back).
 */
export function computeReservationTotal(config, participants) {
  if (!config) return null;
  const n = Number(participants) || 0;
  const basePrice = config.basePrice ?? config.base_price;
  const baseParticipants = config.baseParticipants ?? config.base_participants;
  const extra = config.extraParticipantPrice ?? config.extra_participant_price;
  const perPerson = config.pricePerParticipant ?? config.price_per_participant;
  if (basePrice != null) {
    const included = baseParticipants != null ? Number(baseParticipants) : 0;
    const extraPrice = extra != null ? Number(extra) : 0;
    return Number(basePrice) + Math.max(0, n - included) * extraPrice;
  }
  if (perPerson != null) return Number(perPerson) * n;
  return null;
}

// ─── Auth ────────────────────────────────────────────────────────────────────

export const customerSignupSchema = z.object({
  email: emailSchema,
  password: z.string().min(8, 'Mot de passe : 8 caractères minimum').max(200),
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  phone: moroccanPhoneSchema.nullish(),
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Mot de passe requis').max(200),
});

export const passwordResetRequestSchema = z.object({ email: emailSchema });

export const passwordResetConfirmSchema = z.object({
  token: z.string().trim().min(16, 'Token invalide'),
  newPassword: z.string().min(8, 'Mot de passe : 8 caractères minimum').max(200),
});

// ─── Divers ──────────────────────────────────────────────────────────────────

export const redeemSchema = z.object({ rewardKey: z.string().trim().min(1) });

export const cancelReservationSchema = z.object({
  reason: z.string({ required_error: "Le motif d'annulation est obligatoire" }).trim().min(3),
});
